import React, { useState, useEffect } from 'react';
import { DialogHelp } from 'components';
import {
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Alert,
    Box,
    Typography
} from '@mui/material';
import { getDeleteCount, deleteSelectedItems } from 'api/canshare/canshareApi';
import styles from './DeleteDialog.module.css';

const DeleteDialog = ({
    openDialog,
    onCloseDialog,
    checkedFileDirList,
    userInfo,
    notifyError,
    notifySuccess,
    fetchLatestFileList
}) => {
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteCount, setDeleteCount] = useState(null);
    const [error, setError] = useState(null);

    // Helper function to format delete count messages
    const formatDeleteMessage = (totalFiles, totalFolders) => {
        const parts = [];
        if (totalFiles > 0) {
            parts.push(`${totalFiles} file${totalFiles !== 1 ? 's' : ''}`);
        }
        if (totalFolders > 0) {
            parts.push(`${totalFolders} folder${totalFolders !== 1 ? 's' : ''}`);
        }
        
        if (parts.length === 0) {
            return "No items to delete";
        }
        
        return parts.join(' and ');
    };

    // Extract IDs from checked items
    const getFileIds = () => {
        if (!checkedFileDirList?.files || checkedFileDirList.files.length === 0) {
            return '';
        }
        return checkedFileDirList.files.map(file => file.vv_file_id).join(',');
    };

    const getDirIds = () => {
        if (!checkedFileDirList?.dir || checkedFileDirList.dir.length === 0) {
            return '';
        }
        return checkedFileDirList.dir.map(dir => dir.vv_dir_id).join(',');
    };

    // Fetch delete count when dialog opens
    useEffect(() => {
        if (!openDialog) {
            return;
        }

        const fetchDeleteCount = async () => {
            setLoading(true);
            setError(null);
            setDeleteCount(null);

            try {
                // Extract IDs inside useEffect to avoid dependency issues
                const fileIds = checkedFileDirList?.files?.map(file => file.vv_file_id).join(',') || '';
                const dirIds = checkedFileDirList?.dir?.map(dir => dir.vv_dir_id).join(',') || '';
                const username = userInfo?.userInfo?.email || userInfo?.userInfo?.username || '';

                // Add minimum loading time for better UX
                const [response] = await Promise.all([
                    getDeleteCount(fileIds, dirIds, username),
                    new Promise(resolve => setTimeout(resolve, 800))
                ]);

                if (response.Success) {
                    setDeleteCount(response.Payload); 
                } else {
                    setError(response.Message || 'Failed to get delete count');
                    // If it's an authorization error, notify and close
                    if (response.Message && response.Message.includes('Admin access required')) {
                        notifyError('Admin access required for delete operations');
                        onCloseDialog();
                    }
                }
            } catch (err) {
                console.error('Error fetching delete count:', err);
                setError('Failed to calculate delete count');
            } finally {
                setLoading(false);
            }
        };

        fetchDeleteCount();
    }, [openDialog, checkedFileDirList, userInfo, notifyError, onCloseDialog]);

    // Handle the actual deletion
    const handleDeleteConfirm = async () => {
        setDeleting(true);
        setError(null);

        try {
            const fileIds = getFileIds();
            const dirIds = getDirIds();
            const username = userInfo?.userInfo?.email || userInfo?.userInfo?.username || '';

            const response = await deleteSelectedItems(fileIds, dirIds, username);

            if (response.Success) {
                const successMessage = deleteCount 
                    ? `Successfully deleted ${formatDeleteMessage(deleteCount.TotalFiles, deleteCount.TotalFolders)}`
                    : response.Payload?.Message || 'Items deleted successfully';

                notifySuccess(successMessage);
                onCloseDialog();
                // Refresh the file list
                await fetchLatestFileList();
            } else {
                setError(response.Message || 'Failed to delete items');
                notifyError(response.Message || 'Failed to delete items');
            }
        } catch (err) {
            console.error('Error deleting items:', err);
            setError('Failed to delete items');
            notifyError('Failed to delete items');
        } finally {
            setDeleting(false);
        }
    };

    const renderContent = () => {
        // Loading state
        if (loading) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" p={4}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" mt={2} color="text.secondary">
                        Calculating items to delete...
                    </Typography>
                    <Typography variant="body2" mt={1} color="text.secondary">
                        Please wait while we count nested files and folders.
                    </Typography>
                </Box>
            );
        }

        // Error state
        if (error && !deleteCount) {
            return (
                <Box>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body1" fontWeight="bold">
                            Error calculating delete count
                        </Typography>
                        <Typography variant="body2" mt={1}>
                            {error}
                        </Typography>
                    </Alert>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                        Selected items:
                    </Typography>
                    
                    <List className={styles.list}>
                        {checkedFileDirList?.dir?.map((dir, index) => (
                            <ListItem key={`dir-${index}`} className={styles.list_item}>
                                <ListItemText 
                                    primary={dir.name} 
                                    secondary="Folder"
                                    primaryTypographyProps={{ 
                                        variant: 'body2',
                                        fontSize: '0.875rem'
                                    }}
                                    secondaryTypographyProps={{ 
                                        variant: 'caption',
                                        fontSize: '0.75rem'
                                    }}
                                />
                            </ListItem>
                        ))}
                        {checkedFileDirList?.files?.map((file, index) => (
                            <ListItem key={`file-${index}`} className={styles.list_item}>
                                <ListItemText 
                                    primary={file.name} 
                                    secondary="File"
                                    primaryTypographyProps={{ 
                                        variant: 'body2',
                                        fontSize: '0.875rem'
                                    }}
                                    secondaryTypographyProps={{ 
                                        variant: 'caption',
                                        fontSize: '0.75rem'
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            );
        }

        // Success state - show confirmation with details
        return (
            <>
                <Typography variant="body1" gutterBottom>
                    Are you sure you want to delete the selected files/folders?
                </Typography>
                
                {deleteCount && (
                    <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                            This will permanently delete {formatDeleteMessage(deleteCount.TotalFiles, deleteCount.TotalFolders)}.
                        </Typography>
                        {deleteCount.TotalFiles > 0 && deleteCount.TotalFolders > 0 && (
                            <Typography variant="body2" mt={1} color="text.secondary" fontStyle="italic">
                                Files inside folders will be deleted as well.
                            </Typography>
                        )}
                    </Alert>
                )}

                <Typography variant="body1" color="text.secondary" sx={{ mb: 0 }}>
                    Selected items:
                </Typography>
                
                <List className={styles.list}>
                    {checkedFileDirList?.dir?.map((dir, index) => (
                        <ListItem key={`dir-${index}`} className={styles.list_item}>
                            <ListItemText 
                                primary={dir.name} 
                                secondary="Folder"
                                primaryTypographyProps={{ 
                                    variant: 'body2',
                                    fontSize: '0.875rem'
                                }}
                                secondaryTypographyProps={{ 
                                    variant: 'caption',
                                    fontSize: '0.75rem'
                                }}
                            />
                        </ListItem>
                    ))}
                    {checkedFileDirList?.files?.map((file, index) => (
                        <ListItem key={`file-${index}`} className={styles.list_item}>
                            <ListItemText 
                                primary={file.name} 
                                secondary="File"
                                primaryTypographyProps={{ 
                                    variant: 'body2',
                                    fontSize: '0.875rem'
                                }}
                                secondaryTypographyProps={{ 
                                    variant: 'caption',
                                    fontSize: '0.75rem'
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            </>
        );
    };

    return (
        <DialogHelp
            open={openDialog}
            onClose={onCloseDialog}
            title="Delete Items"
            onSave={handleDeleteConfirm}
            onSaveButtonText={deleting ? 'Deleting...' : 'Delete'}
            onCloseButtonText="Cancel"
            saveButtonDisabled={loading || deleting || (!deleteCount && !error)}
            content={renderContent()}
        />
    );
};

export default DeleteDialog;